/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var METRIKA_SCRIPT_URL_DEFAULT = 'https://mc.yandex.ru/metrika/tag.js';
var METRIKA_SCRIPT_URL_COM = 'https://mc.yandex.com/metrika/tag.js';

function appendScript(src) {
    return new Promise(function (resolve, reject) {
        var script = document.createElement('script');
        script.src = src;
        script.crossOrigin = 'anonymous';
        script.onload = function () {
            resolve();
        };
        script.onerror = function (error) {
            document.head.removeChild(script);
            reject(error);
        };
        document.head.appendChild(script);
    });
}

var delayedBuffer = [];
function clearDelayedBuffer() {
    delayedBuffer = [];
}
function addActionToDelayedBuffer() {
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
    }
    delayedBuffer.push(args);
}
function executeAllActions(callback) {
    delayedBuffer.forEach(function (item) { return callback.apply(null, item); });
    clearDelayedBuffer();
}

function isMetrikaScriptLoaded() {
    var _a;
    return Boolean((_a = window.Ya) === null || _a === void 0 ? void 0 : _a.Metrika2);
}
var loadPromise;
function loadMetrikaScript(url, retriesCount) {
    if (retriesCount === void 0) { retriesCount = 3; }
    if (isMetrikaScriptLoaded()) {
        return Promise.resolve();
    }
    if (loadPromise) {
        return loadPromise;
    }
    loadPromise = new Promise(function (resolve, reject) {
        function load(retriesLeft) {
            appendScript(url)
                .then(function (value) {
                if (isMetrikaScriptLoaded()) {
                    resolve(value);
                }
                else {
                    loadPromise = undefined;
                    reject('Metrika script is loaded but "window.Ya" or "window.Ya.Metrika2" is missing');
                }
            })
                .catch(function (error) {
                if (!retriesLeft) {
                    loadPromise = undefined;
                    reject(error);
                }
                else {
                    window.setTimeout(function () {
                        load(retriesLeft - 1);
                    }, Math.pow(2, (retriesCount - retriesLeft)) * 1000);
                }
            });
        }
        load(retriesCount);
    });
    return loadPromise;
}

var metrikaCounterIds = {};
function getMetrikaCounterIds() {
    return Object.keys(metrikaCounterIds).map(function (id) { return Number(id); });
}
function hasMetrikaCounter(id) {
    return Boolean(window[getMetrikaCounterName(id)]);
}
function getMetrikaCounter(id) {
    return window[getMetrikaCounterName(id)];
}
function getMetrikaCounterName(id) {
    return "yaCounter".concat(id);
}
function destructMetrikaCounter(id) {
    delete metrikaCounterIds[id];
    delete window[getMetrikaCounterName(id)];
}
function initMetrikaCounter(id, options) {
    var counter = getMetrikaCounter(id);
    if (counter) {
        return counter;
    }
    counter = new window.Ya.Metrika2(__assign(__assign({}, options), { id: id }));
    metrikaCounterIds[id] = true;
    window[getMetrikaCounterName(id)] = counter;
    return counter;
}

var isMetrikaScriptLoading = false;
var metrikaScriptUrl = METRIKA_SCRIPT_URL_DEFAULT;
function setMetrikaScriptUrl(url) {
    metrikaScriptUrl = url;
}
function sendAction(counterId, methodName) {
    var args = [];
    for (var _i = 2; _i < arguments.length; _i++) {
        args[_i - 2] = arguments[_i];
    }
    if (typeof window === 'undefined') {
        return;
    }
    if (isMetrikaScriptLoaded()) {
        sendActionWithLoadedMetrika.apply(void 0, __spreadArray([counterId, methodName], args, false));
        return;
    }
    addActionToDelayedBuffer.apply(void 0, __spreadArray([counterId, methodName], args, false));
    if (!isMetrikaScriptLoading) {
        loadMetrikaScript(metrikaScriptUrl)
            .then(function () { return executeAllActions(sendActionWithLoadedMetrika); })
            .catch(function (error) {
            isMetrikaScriptLoading = false;
            console.error(error);
        });
        isMetrikaScriptLoading = true;
    }
}
function sendActionWithLoadedMetrika(counterId, methodName) {
    var args = [];
    for (var _i = 2; _i < arguments.length; _i++) {
        args[_i - 2] = arguments[_i];
    }
    var counter = getMetrikaCounter(counterId);
    if (counter) {
        if (methodName === 'init') {
            return counter;
        }
        try {
            return counter[methodName].apply(counter, args);
        }
        catch (e) {
            console.error(e);
        }
        if (methodName === 'destruct') {
            destructMetrikaCounter(counterId);
        }
    }
    else {
        if (methodName === 'init') {
            return initMetrikaCounter(counterId, args[0]);
        }
    }
    return undefined;
}

function MetrikaCounter(props) {
    if (typeof window === 'undefined') {
        return null;
    }
    sendAction(props.id, 'init', props.options || {});
    return null;
}
function MetrikaCounters(props) {
    if (typeof window === 'undefined') {
        return null;
    }
    props.ids.forEach(function (id) { return sendAction(id, 'init', props.options || {}); });
    return null;
}

var ym = sendAction;

export { METRIKA_SCRIPT_URL_COM, METRIKA_SCRIPT_URL_DEFAULT, MetrikaCounter, MetrikaCounters, clearDelayedBuffer, destructMetrikaCounter, getMetrikaCounter, getMetrikaCounterIds, getMetrikaCounterName, hasMetrikaCounter, initMetrikaCounter, isMetrikaScriptLoaded, loadMetrikaScript, setMetrikaScriptUrl, ym };
